import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, HelpCircle, Phone, Mail, MessageSquare } from 'lucide-react';

const ContactFAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  
  const toggleQuestion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };
  
  const faqs = [
    {
      question: "Quali documenti devo portare per una prima consulenza?",
      answer: "Per una prima consulenza non è necessario portare documenti specifici. Tuttavia, se stai pensando di vendere un immobile, può essere utile avere a portata di mano documenti come l'atto di provenienza, la planimetria catastale, e l'attestato di prestazione energetica (APE), se disponibili."
    },
    {
      question: "La valutazione dell'immobile è gratuita?",
      answer: "Sì, offriamo valutazioni gratuite e senza impegno per il tuo immobile. Il nostro team di esperti valuterà il tuo immobile considerando diversi fattori come la posizione, le dimensioni, lo stato di manutenzione e le tendenze del mercato locale."
    },
    {
      question: "Quanto tempo serve per vendere un immobile?",
      answer: "I tempi di vendita possono variare in base a diversi fattori come la posizione, il prezzo, le condizioni dell'immobile e la situazione del mercato. In media, con la nostra strategia di marketing, riusciamo a vendere gli immobili in 45-60 giorni, significativamente meno della media di mercato che si attesta intorno ai 6 mesi."
    },
    {
      question: "Come posso fissare un appuntamento per vedere un immobile?",
      answer: "Puoi fissare un appuntamento per vedere un immobile in diversi modi: chiamandoci ai numeri +39 327 8855115 o 055 752139, inviando un'email a info@molinaroimmobiliare.it, o utilizzando il modulo di contatto presente su questa pagina. Ti risponderemo nel più breve tempo possibile per organizzare la visita."
    },
    {
      question: "Offrite assistenza per l'ottenimento di un mutuo?",
      answer: "Sì, offriamo consulenza e assistenza completa per l'ottenimento del mutuo. Collaboriamo con diversi istituti di credito e possiamo aiutarti a trovare la soluzione più adatta alle tue esigenze finanziarie, seguendoti in ogni fase del processo."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5 }
    }
  };
  
  return (
    <section className="py-24 bg-gray-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute -top-20 -right-20 w-80 h-80 bg-secondary opacity-5 rounded-full"></div>
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-primary opacity-5 rounded-full"></div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="inline-block bg-secondary/10 text-secondary px-4 py-1 rounded-full text-sm font-semibold mb-3">
            Hai Delle Domande?
          </span>
          <h2 className="text-4xl font-monomakh text-primary mb-4">Domande Frequenti</h2>
          <p className="text-gray-600 text-lg">
            Abbiamo raccolto alcune delle domande più frequenti che riceviamo dai nostri clienti.
            Se non trovi la risposta che cerchi, non esitare a contattarci direttamente.
          </p>
        </motion.div>
        
        <motion.div 
          className="max-w-3xl mx-auto" 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="mb-4"
            >
              <div 
                className={`bg-white rounded-xl shadow-sm overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'shadow-md' : 'hover:shadow-md'
                }`}
              >
                <button
                  onClick={() => toggleQuestion(index)}
                  className="w-full px-6 py-5 text-left flex justify-between items-center focus:outline-none"
                >
                  <div className="flex items-center pr-4">
                    <div className="bg-primary/10 p-2 rounded-full mr-4">
                      <HelpCircle className="h-5 w-5 text-primary" />
                    </div>
                    <h3 className="font-semibold text-primary text-lg">{faq.question}</h3>
                  </div>
                  
                  <div className="flex-shrink-0">
                    {openIndex === index ? (
                      <ChevronUp className="h-5 w-5 text-primary" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-primary" />
                    )}
                  </div>
                </button>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden border-t border-gray-100"
                    >
                      <div className="px-6 py-5 bg-gray-50">
                        <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="text-center mt-16"
        >
          <p className="text-gray-600 mb-8 text-lg">
            Non hai trovato la risposta che cercavi? Contattaci direttamente.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:+393278855115" 
              className="inline-flex items-center justify-center bg-primary hover:bg-primary-light text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-md"
            >
              <Phone className="h-5 w-5 mr-2" />
              Chiamaci
            </a>
            <a 
              href="mailto:info@molinaroimmobiliare.it" 
              className="inline-flex items-center justify-center bg-secondary hover:bg-secondary-light text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-md"
            >
              <Mail className="h-5 w-5 mr-2" />
              Scrivici
            </a>
            <a 
              href="#form" 
              className="inline-flex items-center justify-center border border-primary text-primary hover:bg-primary hover:text-white px-6 py-3 rounded-lg font-semibold transition-colors shadow-md"
            >
              <MessageSquare className="h-5 w-5 mr-2" />
              Usa il Form
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ContactFAQ;